import { Component, OnInit } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';
import { Params, ActivatedRoute, Router } from '@angular/router';
import { AdminData } from '../_models/admin';
import { CountryData } from '../_models/country';
import { CurrencyData } from '../_models/currency';
import { Counts } from '../_models/data';
import { DealslipDashboard, DealslipData } from '../_models/dealslip';
import { UserData } from '../_models/users';
import { ApiService } from '../_services/api.service';
import { ChecksService } from '../_services/checks.service';
import { NotificationService } from '../_services/notification.service';

@Component({
  selector: 'app-dealslip',
  templateUrl: './dealslip.component.html',
  styleUrl: './dealslip.component.css'
})
export class DealslipComponent implements OnInit {
  routeParams: Params;
  queryParams: Params;

  page: number = 1;
  count: Counts;

  url = this.router.url.slice(1);

  mainHeader: string = 'List All Dealslips';
  formHeader: string = 'Create Dealslip';
  view: string = '';

  processing: boolean = false;
  loading: boolean = true;
  searchLoading: boolean = false;
  dashboardLoading: boolean = false;
  buttonText: string = 'Create Dealslip';
  searchMode: boolean = false;
  activeSearchPayload: any = null;

  countryList: CountryData[] = [];
  activeCurrencies: CurrencyData[] = [];
  dealslipList: DealslipData[] = [];
  oneDealslip: DealslipData;
  dashboardData: DealslipDashboard;

  edit: boolean = false;
  viewOne: boolean = false;
  dealslipRef: number = 0;
  dealslipName: string = '';
  userOptions: { id: number, label: string }[] = [];
  adminOptions: { id: number, label: string }[] = [];
  clientLabel: string = '';
  searchUserLabel: string = '';
  searchCreatedAdminLabel: string = '';
  searchModifiedAdminLabel: string = '';
  dashboardUserLabel: string = '';
  dashboardCreatedAdminLabel: string = '';
  dashboardModifiedAdminLabel: string = '';

  dataForm = this.fb.group<any>({
    ref: [0],
    customerType: ['walk-in', Validators.required],
    client: [null],
    walkInFirstName: [''],
    walkInLastName: [''],
    walkInPhone: [''],
    walkInEmail: ['', Validators.email],
    sellAmount: [null, Validators.required],
    sellCurrency: ['', Validators.required],
    buyAmount: [null, Validators.required],
    buyCurrency: ['', Validators.required],
    date: ['', Validators.required],
    rate: [null, Validators.required],
    status: ['pending', Validators.required],
  }, {});

  searchForm = this.fb.group<any>({
    dateFrom: [''],
    dateTo: [''],
    status: [''],
    sellCurrency: [''],
    buyCurrency: [''],
    user: [null],
    createdAdmin: [null],
    modifiedAdmin: [null],
  }, {});

  dashboardForm = this.fb.group<any>({
    dateFrom: [''],
    dateTo: [''],
    user: [null],
    createdAdmin: [null],
    modifiedAdmin: [null],
  }, {});

  constructor(
    private fb: FormBuilder,
    public checkService: ChecksService,
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private notifyService: NotificationService,
    private router: Router,
  ) {
    this.checkService.checkSession();
    this.oneDealslip = new DealslipData();
    this.dashboardData = new DealslipDashboard();
  }

  get ref() { return this.dataForm.get('ref'); }
  get client() { return this.dataForm.get('client'); }
  get customerType() { return this.dataForm.get('customerType'); }
  get sellAmount() { return this.dataForm.get('sellAmount'); }
  get sellCurrency() { return this.dataForm.get('sellCurrency'); }
  get buyAmount() { return this.dataForm.get('buyAmount'); }
  get buyCurrency() { return this.dataForm.get('buyCurrency'); }
  get date() { return this.dataForm.get('date'); }
  get rate() { return this.dataForm.get('rate'); }
  get status() { return this.dataForm.get('status'); }

  ngOnInit(): void {
	this.configureCustomerValidation(String(this.dataForm.value.customerType || 'walk-in'));
	this.customerType.valueChanges.subscribe(value => this.configureCustomerValidation(String(value)));
    this.setupRateAutoCalculation();
    this.getCountries();
    this.getActiveCurrencies();
    this.loadUserOptions();
    this.loadAdminOptions();
    this.getRouteParams();
  }

  getActiveCurrencies() {
    this.apiService.getActiveCurrencies().subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success == true) {
        this.activeCurrencies = data.data || [];
      }
    });
  }

  getCountries() {
    this.apiService.getCountries(0, 'list').subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        if (data.success == true) {
          this.countryList = data.data;
        } else {
          this.notifyService.showError(data.error.message, 'Error');
        }
      }
    );
  }

  loadUserOptions(page:number = 1, collected: { id: number, label: string }[] = []) {
    this.apiService.getUser(page, '').subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        if (data.success == true) {
          const current = (data.data || []).map((row: UserData) => ({
            id: row.ref,
            label: this.buildUserOptionLabel(row),
          }));
          const merged = [...collected, ...current];

          if (data.counts && data.counts.currentPage < data.counts.totalPage) {
            this.loadUserOptions(data.counts.currentPage + 1, merged);
          } else {
            this.userOptions = this.uniqueOptions(merged);
          }
        }
      }
    );
  }

  loadAdminOptions(page:number = 1, collected: { id: number, label: string }[] = []) {
    this.apiService.listAdministrator(page, '').subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        if (data.success == true) {
          const current = (data.data || []).map((row: AdminData) => ({
            id: row.ref,
            label: this.buildAdminOptionLabel(row),
          }));
          const merged = [...collected, ...current];

          if (data.counts && data.counts.currentPage < data.counts.totalPage) {
            this.loadAdminOptions(data.counts.currentPage + 1, merged);
          } else {
            this.adminOptions = this.uniqueOptions(merged);
          }
        }
      }
    );
  }

  listDealslip(page:number) {
    this.loading = true;
    this.searchMode = false;
    this.activeSearchPayload = null;

    this.apiService.getDealslip(page, this.view).subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        this.loading = false;
        if (data.success == true) {
          this.count = data.counts;
          this.dealslipList = data.data;
        } else {
          this.notifyService.showError(data.error.message, 'Error');
        }
      }
    );
  }

  onCreate() {
    const data: any = {
      sellAmount: this.dataForm.value.sellAmount,
      sellCurrency: this.dataForm.value.sellCurrency,
      buyAmount: this.dataForm.value.buyAmount,
      buyCurrency: this.dataForm.value.buyCurrency,
      date: this.normalizeDateTime(this.dataForm.value.date as string),
      rate: this.dataForm.value.rate,
      status: this.edit === true ? this.dataForm.value.status : 'pending',
    };
	if (this.dataForm.value.customerType === 'onboarded') {
	  data.client = this.dataForm.value.client;
	} else {
	  data.walkInCustomer = {
		firstName: this.dataForm.value.walkInFirstName,
		lastName: this.dataForm.value.walkInLastName,
		phone: this.dataForm.value.walkInPhone,
		email: this.dataForm.value.walkInEmail,
	  };
	}

    if (this.edit === true) {
      data['ref'] = this.dataForm.value.ref;
      this.editDealslip(data);
    } else {
      this.createDealslip(data);
    }
  }

  createDealslip(data) {
    this.processing = true;
    this.buttonText = 'Creating Dealslip...';

    this.apiService.createDealslip(data).subscribe(
      row => {
        this.checkService.checkLoggedin(row);
        this.processing = false;
        if (row.success == true) {
          this.notifyService.showSuccess('Dealslip created successfully', 'Dealslip Created');
          this.checkService.closeModal('modal-dealslip-form');
          this.onCancel();
          this.listDealslip(this.page);
          this.loadDashboard();
        } else {
          this.notifyService.showError(row.error.message + '. ' + row.error.additional_message, 'Error');
        }
        this.buttonText = 'Create Dealslip';
      }
    );
  }

  editDealslip(data) {
    this.processing = true;
    this.buttonText = 'Saving Changes...';

    this.apiService.editDealslip(data).subscribe(
      row => {
        this.checkService.checkLoggedin(row);
        this.processing = false;
        if (row.success == true) {
          this.notifyService.showSuccess('Dealslip updated successfully', 'Dealslip Edited');
          this.checkService.closeModal('modal-dealslip-form');
          this.onCancel();
          this.listDealslip(this.page);
          this.loadDashboard();
        } else {
          this.notifyService.showError(row.error.message + '. ' + row.error.additional_message, 'Error');
        }
        this.buttonText = 'Create Dealslip';
      }
    );
  }

  viewDealslip(data: DealslipData) {
    this.viewOne = true;
    this.oneDealslip = data;
  }

  closeDealslipDrawer() {
    this.viewOne = false;
    this.oneDealslip = null;
  }

  getEdit(data: DealslipData) {
    this.viewOne = false;
    this.clientLabel = this.buildDealslipClientOptionLabel(data);
    this.dataForm.patchValue({
      ref: data.ref,
	  customerType: data.client?.customerType === 'walk-in' ? 'walk-in' : 'onboarded',
	  client: data.client?.customerType === 'walk-in' ? null : data.client?.ref,
	  walkInFirstName: data.client?.customerType === 'walk-in' ? data.client.firstName : '',
	  walkInLastName: data.client?.customerType === 'walk-in' ? data.client.lastName : '',
	  walkInPhone: data.client?.customerType === 'walk-in' ? data.client.phone : '',
	  walkInEmail: data.client?.customerType === 'walk-in' ? data.client.email : '',
      sellAmount: data.sellAmount,
      sellCurrency: data.sellCurrency?.code,
      buyAmount: data.buyAmount,
      buyCurrency: data.buyCurrency?.code,
      date: this.toDateTimeLocalValue(data.date),
      rate: data.rate,
      status: this.resolveStatus(data),
    }, { emitEvent: false });
    this.edit = true;
    this.formHeader = 'Edit Dealslip #' + data.ref;
    this.buttonText = 'Edit Dealslip';
  }

  onCancel() {
    this.edit = false;
    this.viewOne = false;
    this.clientLabel = '';
    this.dataForm.reset({
      ref: 0,
	  customerType: 'walk-in',
      client: null,
	  walkInFirstName: '',
	  walkInLastName: '',
	  walkInPhone: '',
	  walkInEmail: '',
      sellAmount: null,
      sellCurrency: '',
      buyAmount: null,
      buyCurrency: '',
      date: '',
      rate: null,
      status: 'pending',
    });
    this.formHeader = 'Create Dealslip';
    this.buttonText = 'Create Dealslip';
  }

  onSearch(page:number = 1) {
    this.searchLoading = true;
    this.loading = true;
    this.searchMode = true;

    const payload = this.buildSearchPayload();
    this.activeSearchPayload = payload;
    this.syncDashboardFiltersFromSearch();

    this.apiService.searchDealslip(payload, page).subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        this.searchLoading = false;
        this.loading = false;
        if (data.success == true) {
          this.page = page;
          this.count = data.counts;
          this.dealslipList = data.data;
          this.mainHeader = 'Dealslip Search Results';
          this.loadDashboard(this.buildDashboardPayload());
        } else {
          this.notifyService.showError(data.error.message + '. ' + data.error.additional_message, 'Error');
        }
      }
    );
  }

  resetSearch() {
    this.searchForm.reset({
      dateFrom: '',
      dateTo: '',
      status: '',
      sellCurrency: '',
      buyCurrency: '',
      user: null,
      createdAdmin: null,
      modifiedAdmin: null,
    });
    this.searchUserLabel = '';
    this.searchCreatedAdminLabel = '';
    this.searchModifiedAdminLabel = '';
    this.searchMode = false;
    this.activeSearchPayload = null;
    this.refreshRouteDrivenData();
  }

  loadDashboard(payload: any = null) {
    this.dashboardLoading = true;

    const requestPayload = payload ?? this.buildDashboardPayload();

    this.apiService.dealslipDashboard(requestPayload).subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        this.dashboardLoading = false;
        if (data.success == true) {
          this.dashboardData = data;
        } else {
          this.notifyService.showError(data.error.message + '. ' + data.error.additional_message, 'Error');
        }
      }
    );
  }

  resetDashboard() {
    this.dashboardForm.reset({
      dateFrom: '',
      dateTo: '',
      user: null,
      createdAdmin: null,
      modifiedAdmin: null,
    });
    this.dashboardUserLabel = '';
    this.dashboardCreatedAdminLabel = '';
    this.dashboardModifiedAdminLabel = '';
    if (this.searchMode) {
      this.onSearch(1);
      return;
    }
    this.loadDashboard();
  }

  changeDealslipStatus(status:string, ref:number) {
    if (!confirm('Are you sure you want to update this dealslip status?')) {
      return;
    }

    this.processing = true;
    this.apiService.changeDealslipStatus({ ref, status }).subscribe(
      row => {
        this.checkService.checkLoggedin(row);
        this.processing = false;
        if (row.success == true) {
          this.notifyService.showSuccess('Dealslip status updated successfully', 'Dealslip Modified');
          this.checkService.closeModal();
          if (this.searchMode) {
            this.onSearch(this.page);
          } else {
            this.listDealslip(this.page);
            this.loadDashboard();
          }
        } else {
          this.notifyService.showError(row.error.message + '. ' + row.error.additional_message, 'Error');
        }
      }
    );
  }

  deleteDealslip(ref:number) {
    if (!confirm('Are you sure you want to remove this dealslip?')) {
      return;
    }

    this.processing = true;
    this.apiService.deleteDealslip(ref).subscribe(
      row => {
        this.checkService.checkLoggedin(row);
        this.processing = false;
        if (row.success == true) {
          this.notifyService.showSuccess('Dealslip removed successfully', 'Dealslip Deleted');
          this.checkService.closeModal();
          if (this.searchMode) {
            this.onSearch(this.page);
          } else {
            this.listDealslip(this.page);
            this.loadDashboard();
          }
        } else {
          this.notifyService.showError(row.error.message + '. ' + row.error.additional_message, 'Error');
        }
      }
    );
  }

  getDataFromList(name:string, ref:number) {
    this.dealslipName = name;
    this.dealslipRef = ref;
  }

  get currencyOptions() {
    return this.activeCurrencies.filter((currency, index, list) => currency?.code && list.findIndex(item => item.code === currency.code) === index);
  }

  get formMarketRate(): string {
    return this.formatMarketRate(
      Number(this.dataForm.value.sellAmount),
      String(this.dataForm.value.sellCurrency || ''),
      Number(this.dataForm.value.buyAmount),
      String(this.dataForm.value.buyCurrency || ''),
    );
  }

  dealslipMarketRate(dealslip: DealslipData): string {
    return this.formatMarketRate(
      Number(dealslip?.sellAmount),
      String(dealslip?.sellCurrency?.code || ''),
      Number(dealslip?.buyAmount),
      String(dealslip?.buyCurrency?.code || ''),
    );
  }

  private formatMarketRate(sellAmount: number, sellCode: string, buyAmount: number, buyCode: string): string {
    sellCode = sellCode.trim().toUpperCase();
    buyCode = buyCode.trim().toUpperCase();
    if (!sellCode || !buyCode || sellCode === buyCode || !Number.isFinite(sellAmount) || !Number.isFinite(buyAmount) || sellAmount <= 0 || buyAmount <= 0) {
      return '';
    }

    const buyPerSell = buyAmount / sellAmount;
    const sellPerBuy = sellAmount / buyAmount;
    const format = (value: number) => new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    }).format(value);

    if (buyPerSell >= sellPerBuy) {
      return `${sellCode} 1 → ${buyCode} ${format(buyPerSell)} (${buyCode} 1 → ${sellCode} ${format(sellPerBuy)})`;
    }
    return `${buyCode} 1 → ${sellCode} ${format(sellPerBuy)} (${sellCode} 1 → ${buyCode} ${format(buyPerSell)})`;
  }

  printClient(data: DealslipData): string {
    if (!data?.client) {
      return 'N/A';
    }
    return data.client.firstName + ' ' + data.client.lastName + ' (#' + data.client.ref + ')';
  }

  printAdminName(data): string {
    if (!data) {
      return 'N/A';
    }
    return (data.firstName || '') + ' ' + (data.lastName || '');
  }

  hasManageOptions(data: DealslipData): boolean {
    return !this.isDeletedDealslip(data);
  }

  canMarkNew(data: DealslipData): boolean {
    const status = this.getDealslipStatusValue(data);
    return status !== 'deleted' && status !== 'new' && status !== 'pending' && status !== 'complete';
  }

  canMarkComplete(data: DealslipData): boolean {
    const status = this.getDealslipStatusValue(data);
    return status !== 'deleted' && status !== 'complete';
  }

  canMarkCancel(data: DealslipData): boolean {
    const status = this.getDealslipStatusValue(data);
    return status !== 'deleted' && status !== 'cancel' && status !== 'complete';
  }

  canDeleteDealslip(data: DealslipData): boolean {
    const status = this.getDealslipStatusValue(data);
    return status !== 'deleted' && status !== 'complete' && status !== 'cancel';
  }

  routePage(page:number) {
    if (this.searchMode) {
      this.onSearch(page);
    } else {
      this.listDealslip(page);
    }
  }

  onClientChange(value: string) {
    this.clientLabel = value;
	const selected = this.userOptions.find(option => option.label === value);
	if (selected) {
	  this.dataForm.patchValue({
		customerType: 'onboarded', client: selected.id,
		walkInFirstName: '', walkInLastName: ''
	  });
	  return;
	}
	const parts = value.trim().replace(/\s+/g, ' ').split(' ');
	this.dataForm.patchValue({
	  customerType: 'walk-in', client: null,
	  walkInFirstName: parts.shift() || '', walkInLastName: parts.join(' ')
	});
  }

  onSearchUserChange(value: string) {
    this.searchUserLabel = value;
    this.applySelectedOption(value, this.userOptions, this.searchForm, 'user');
  }

  onSearchCreatedAdminChange(value: string) {
    this.searchCreatedAdminLabel = value;
    this.applySelectedOption(value, this.adminOptions, this.searchForm, 'createdAdmin');
  }

  onSearchModifiedAdminChange(value: string) {
    this.searchModifiedAdminLabel = value;
    this.applySelectedOption(value, this.adminOptions, this.searchForm, 'modifiedAdmin');
  }

  onDashboardUserChange(value: string) {
    this.dashboardUserLabel = value;
    this.applySelectedOption(value, this.userOptions, this.dashboardForm, 'user');
  }

  onDashboardCreatedAdminChange(value: string) {
    this.dashboardCreatedAdminLabel = value;
    this.applySelectedOption(value, this.adminOptions, this.dashboardForm, 'createdAdmin');
  }

  onDashboardModifiedAdminChange(value: string) {
    this.dashboardModifiedAdminLabel = value;
    this.applySelectedOption(value, this.adminOptions, this.dashboardForm, 'modifiedAdmin');
  }

  getRouteParams() {
    this.activatedRoute.params.subscribe(params => {
      this.routeParams = params;
      this.refreshRouteDrivenData();
    });

    this.activatedRoute.queryParams.subscribe(params => {
      this.queryParams = params;
      this.refreshRouteDrivenData();
    });
  }

  private cleanPayload(data: any) {
    const payload = {};
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value !== null && value !== undefined && value !== '') {
        payload[key] = value;
      }
    });
    return payload;
  }

  private buildSearchPayload() {
    return this.cleanPayload({
      dateFrom: this.searchForm.value.dateFrom,
      dateTo: this.searchForm.value.dateTo,
      status: this.searchForm.value.status,
      sellCurrency: this.searchForm.value.sellCurrency,
      buyCurrency: this.searchForm.value.buyCurrency,
      user: this.searchForm.value.user,
      createdAdmin: this.searchForm.value.createdAdmin,
      modifiedAdmin: this.searchForm.value.modifiedAdmin,
    });
  }

  private buildDashboardPayload() {
    return this.cleanPayload({
      dateFrom: this.dashboardForm.value.dateFrom,
      dateTo: this.dashboardForm.value.dateTo,
      status: this.searchMode ? this.searchForm.value.status : (this.view || null),
      sellCurrency: this.searchMode ? this.searchForm.value.sellCurrency : null,
      buyCurrency: this.searchMode ? this.searchForm.value.buyCurrency : null,
      user: this.dashboardForm.value.user,
      createdAdmin: this.dashboardForm.value.createdAdmin,
      modifiedAdmin: this.dashboardForm.value.modifiedAdmin,
    });
  }

  private syncDashboardFiltersFromSearch() {
    this.dashboardForm.patchValue({
      dateFrom: this.searchForm.value.dateFrom,
      dateTo: this.searchForm.value.dateTo,
      user: this.searchForm.value.user,
      createdAdmin: this.searchForm.value.createdAdmin,
      modifiedAdmin: this.searchForm.value.modifiedAdmin,
    });
    this.dashboardUserLabel = this.searchUserLabel;
    this.dashboardCreatedAdminLabel = this.searchCreatedAdminLabel;
    this.dashboardModifiedAdminLabel = this.searchModifiedAdminLabel;
  }

  private setupRateAutoCalculation() {
    this.sellAmount.valueChanges.subscribe(() => this.updateRateFromAmounts());
    this.buyAmount.valueChanges.subscribe(() => this.updateRateFromAmounts());
	this.rate.valueChanges.subscribe(() => this.updateBuyAmountFromRate());
  }

  updateRateFromAmounts() {
    const sell = Number(this.dataForm.value.sellAmount);
    const buy = Number(this.dataForm.value.buyAmount);

    if (!Number.isFinite(sell) || !Number.isFinite(buy) || sell <= 0 || buy <= 0) {
      this.dataForm.patchValue({ rate: null }, { emitEvent: false });
      return;
    }

    this.dataForm.patchValue({
      rate: Number((buy / sell).toFixed(6)),
    }, { emitEvent: false });
  }

  updateBuyAmountFromRate() {
	const sell = Number(this.sellAmount.value);
	const rate = Number(this.rate.value);
	this.buyAmount.setValue(sell > 0 && rate > 0 ? Number((sell * rate).toFixed(4)) : null, { emitEvent: false });
  }

  private configureCustomerValidation(type: string) {
	if (type === 'onboarded') {
	  this.client.setValidators(Validators.required);
	  this.dataForm.get('walkInFirstName')?.clearValidators();
	} else {
	  this.client.clearValidators();
	  this.dataForm.get('walkInFirstName')?.setValidators(Validators.required);
	}
	this.client.updateValueAndValidity({ emitEvent: false });
	this.dataForm.get('walkInFirstName')?.updateValueAndValidity({ emitEvent: false });
  }

  private applySelectedOption(value: string, options: { id: number, label: string }[], form: any, controlName: string) {
    const selected = options.find((option) => option.label === value);
    form.patchValue({
      [controlName]: selected ? selected.id : null,
    });
  }

  private normalizeDateTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value.replace('T', ' ') + ':00';
  }

  private toDateTimeLocalValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value.replace(' ', 'T').slice(0, 16);
  }

  private resolveStatus(data: DealslipData): string {
    if (data?.status?.pending) return 'pending';
    if (data?.status?.complete) return 'complete';
    if (data?.status?.cancel) return 'cancel';
    if (data?.status?.new) return 'new';
    if (data?.status?.inactive) return 'inactive';
    if (data?.status?.active) return 'active';
    return 'pending';
  }

  private getDealslipStatusValue(data: DealslipData): string {
    return this.resolveStatus(data);
  }

  private isDeletedDealslip(data: DealslipData): boolean {
    return !!data?.status?.deleted;
  }

  private capitalizeFirstLetter(text: string): string {
    if (!text) {
      return '';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private refreshRouteDrivenData() {
    this.view = this.routeParams && this.routeParams['view'] !== undefined ? this.routeParams['view'] : '';
    this.mainHeader = this.view !== ''
      ? 'List ' + this.capitalizeFirstLetter(this.view) + ' Dealslips'
      : 'List All Dealslips';
    this.page = this.queryParams && this.queryParams['page'] !== undefined ? +this.queryParams['page'] : 1;

    if (this.searchMode) {
      return;
    }

    this.listDealslip(this.page);
    this.loadDashboard();
  }

  private buildUserOptionLabel(row: UserData): string {
    const name = row.companyDetails?.ref > 0
      ? row.companyDetails.name
      : ((row.firstName || '') + ' ' + (row.lastName || '')).trim();

    return name + ' (#' + row.ref + ')';
  }

  private buildDealslipClientOptionLabel(data: DealslipData): string {
    const name = ((data.client?.firstName || '') + ' ' + (data.client?.lastName || '')).trim();
    return (name || data.client?.email || 'Client') + ' (#' + data.client?.ref + ')';
  }

  private buildAdminOptionLabel(row: AdminData): string {
    return ((row.firstName || '') + ' ' + (row.lastName || '')).trim() + ' (#' + row.ref + ')';
  }

  private uniqueOptions(options: { id: number, label: string }[]) {
    return options.filter((option, index, list) => list.findIndex((item) => item.id === option.id) === index);
  }
}
